<script setup lang="ts">
import type { ElDropdown } from 'element-plus'
import type { UserNotification } from '@/entities/notifications'
import { useIntersectionObserver } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/entities/auth'
import {
  CNotificationDetailModal,
  useNotificationsStore,
} from '@/entities/notifications'
import { useStoresStore } from '@/entities/store'
import { useCreateCompanyModal } from '@/features/autobidder'
import { HttpError } from '@/shared/api/errors'
import { useCustomToast } from '@/shared/lib'
import { formatDateDisplay } from '@/shared/lib/formatters'
import { clearAllStorageOnLogout, resetAllStores } from '@/shared/lib/utils'
import { CAvatar, CButton, CDialog, CLanguageSwitcher } from '@/shared/ui'
import CUpdateApiKeyModal from '@/shared/ui/CUpdateApiKeyModal.vue'

// Define emits
defineEmits<{
  toggleSidebar: []
}>()

const { t } = useI18n()
const { success, error: showError } = useCustomToast()

// Reactive storeId — so header UI (e.g. autobidder "create" button) reacts to late hydration
const { currentStoreId } = storeToRefs(useStoresStore())
const hasStoreId = computed(() => !!currentStoreId.value)
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

const isDashboardPage = computed(() => {
  return route.name === 'Dashboard'
})

const showLogOutModal = ref(false)
const showApiKeyModal = ref(false)
const dropdownRef = ref<InstanceType<typeof ElDropdown>>()
const notificationsDropdownRef = ref<InstanceType<typeof ElDropdown>>()

// Notifications store
const notificationsStore = useNotificationsStore()
const recentNotifications = computed(
  () => notificationsStore.recentNotifications,
)
const isLoadingNotifications = computed(() => notificationsStore.isLoading)
const isLoadingMore = computed(() => notificationsStore.isLoadingMore)
const hasMore = computed(() => notificationsStore.hasMore)
const unreadCount = computed(() => notificationsStore.unreadCount)

// Notification detail modal
const selectedNotification = ref<UserNotification | null>(null)
const showNotificationModal = ref(false)

// Intersection Observer for load more
const loadMoreTrigger = ref<HTMLElement | null>(null)
const scrollContainer = ref<HTMLElement | null>(null)
const isMarkingAllAsRead = ref(false)
const isDropdownVisible = ref(false)

// Get modal control from composable
const { openModal } = useCreateCompanyModal()

const title = computed(() => {
  return (route.meta.title ?? '') as string
})

const isAutoBidderPage = computed(() => {
  return route.path === '/auto-bidder'
})

const isAuthenticated = computed(() => authStore.isAuthenticated)
const userFullName = computed(() => authStore.userFullName)
const userEmail = computed(() => authStore.userEmail)

// Tariff data from profile
const isTrial = computed(() => authStore.profile?.current_tariff?.is_trial === true)

const tariffDisplayName = computed(() => {
  return authStore.profile?.current_tariff?.tariff?.name || t('essential')
})

const tariffDuration = computed(() => {
  const currentTariff = authStore.profile?.current_tariff

  if (!currentTariff) 
    return t('unlimited')

  // If end_date is null, show "Бессрочно"
  if (!currentTariff.end_date) {
    return 'Бессрочно'
  }

  // Backend returns ISO 8601 datetime — native Date parser handles it.
  const endDate = new Date(currentTariff.end_date)
  const now = new Date()

  const diffMs = endDate.getTime() - now.getTime()

  if (diffMs <= 0) {
    return 'Истёк'
  }

  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24))
  return `${diffDays} ${diffDays === 1 ? 'день' : diffDays < 5 ? 'дня' : 'дней'}`
})

// Computed properties for tariff button logic
const currentTariffType = computed(() => {
  return (
    authStore.profile?.current_tariff?.tariff?.type
    || authStore.profile?.current_tariff?.tariff?.name
    || 'essential'
  )
})

const isTariffExpired = computed(() => {
  const currentTariff = authStore.profile?.current_tariff

  if (!currentTariff || !currentTariff.end_date) {
    return false
  }

  const endDate = new Date(currentTariff.end_date)
  const now = new Date()

  return endDate.getTime() <= now.getTime()
})

const isEssential = computed(() => {
  const tariffType = currentTariffType.value.toLowerCase()
  return (
    tariffType === 'essential'
    || tariffType === 'эссенциал'
    || tariffType === 'бесплатный'
  )
})

const shouldShowTariffButton = computed(() => {
  // Show button for Essential or Premium expired
  return (
    isEssential.value
    || (currentTariffType.value.toLowerCase().includes('premium')
      && isTariffExpired.value)
  )
})

const tariffButtonText = computed(() => {
  if (isTariffExpired.value) {
    return 'Продлить'
  }
  return 'Купить'
})

function handleOpenCreateCompanyModal() {
  openModal()
}

function handleTariffButtonClick() {
  router.push('/tariffs')
}

function navigateTo(path: string) {
  dropdownRef.value?.handleClose()
  notificationsDropdownRef.value?.handleClose()
  router.push(path)
}

function formatNotificationDate(dateString: string): string {
  try {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) 
      return t('just_now')
    if (diffMins < 60)
      return t('minutes_ago').replace('{count}', diffMins.toString())
    if (diffHours < 24)
      return t('hours_ago').replace('{count}', diffHours.toString())
    if (diffDays < 7)
      return t('days_ago').replace('{count}', diffDays.toString())

    return formatDateDisplay(date)
  }
  catch {
    return dateString
  }
}

function handleNotificationsDropdownVisible(visible: boolean): void {
  isDropdownVisible.value = visible
  if (visible) {
    // Only fetch if cache is invalid or empty, otherwise reuse cached data
    notificationsStore.fetchRecentNotifications(true, false)
  }
}

// Use VueUse intersection observer
useIntersectionObserver(
  loadMoreTrigger,
  ([{ isIntersecting }]) => {
    if (
      isIntersecting
      && hasMore.value
      && !isLoadingMore.value
      && isDropdownVisible.value
    ) {
      notificationsStore.loadMoreNotifications()
    }
  },
  {
    root: scrollContainer,
    rootMargin: '0px',
    threshold: 0.1,
  },
)

async function handleMarkAllAsRead(): Promise<void> {
  try {
    isMarkingAllAsRead.value = true
    await notificationsStore.markAllAsRead()
    // Invalidate cache and refresh unread count from API
    notificationsStore.invalidateCache()
    await notificationsStore.refreshUnreadCount()
    success({
      title: t('success'),
      message: t('all_notifications_marked_as_read'),
    })
  }
  catch (err) {
    // If it's an HttpError, the global error handler will show the error
    // Don't show a duplicate toast here
    if (err instanceof HttpError && err.errorData) {
      // Global handler will take care of it
      return
    }
    showError({
      title: t('error'),
      message:
        err instanceof Error ? err.message : t('failed_to_mark_all_as_read'),
    })
  }
  finally {
    isMarkingAllAsRead.value = false
  }
}

async function handleNotificationClick(
  notification: UserNotification,
): Promise<void> {
  // Close dropdown first
  notificationsDropdownRef.value?.handleClose()

  // Mark as read if unread (before opening modal)
  if (!notification.is_read) {
    try {
      const { markNotificationAsReadRequest } = await import(
        '@/entities/notifications',
      )
      await markNotificationAsReadRequest({
        notification_ids: [notification.id],
      })
      notificationsStore.updateNotificationReadStatus(notification.id)
      // Refresh unread count from API and invalidate cache
      notificationsStore.invalidateCache()
      await notificationsStore.refreshUnreadCount()
    }
    catch (err) {
      console.error('Error marking notification as read:', err)
    }
  }

  // Open modal with notification details
  selectedNotification.value = notification
  showNotificationModal.value = true
}

function handleViewAllClick(): void {
  notificationsDropdownRef.value?.handleClose()
  router.push('/notifications')
}

// Watch for route changes to refresh notifications when on notifications page
watch(
  () => route.path,
  () => {
    if (route.path === '/notifications') {
      // Force refresh when navigating to notifications page
      notificationsStore.fetchRecentNotifications(true, true)
    }
  },
)

// Watch for authentication state to fetch notifications
watch(
  () => isAuthenticated.value,
  (authenticated) => {
    if (authenticated) {
      // Only fetch if cache is invalid
      notificationsStore.fetchRecentNotifications(true, false)
    }
    else {
      notificationsStore.clearNotifications()
    }
  },
  { immediate: true },
)

// Fetch notifications on mount if authenticated and cache is invalid
onMounted(() => {
  if (isAuthenticated.value) {
    notificationsStore.fetchRecentNotifications(true, false)
  }
})

function handleLogout() {
  showLogOutModal.value = false

  // Reset all Pinia stores
  resetAllStores()

  // Clear auth tokens AND the TanStack Query cache (the store's logout()
  // handles queryClient.removeQueries() internally, so we don't need to
  // enumerate query keys here anymore).
  authStore.logout()

  // Clear all browser storage (cookies, localStorage, sessionStorage)
  clearAllStorageOnLogout()

  // Show success toast
  success({
    title: t('successful_logout'),
    message: t('successful_logout_message'),
  })

  router.push({ name: 'Home' })
}
</script>

<template>
  <!-- Desktop Header (sm+) - existing layout wrapped -->
  <div
    class="hidden sm:flex items-center justify-between py-6 px-4 flex-wrap gap-y-3"
  >
    <div class="flex items-center gap-2 sm:gap-4 min-w-0 shrink-0">
      <h1 class="sm:text-2xl text-xl font-bold mt-2 sm:mt-0 whitespace-nowrap">
        {{ t(title) }}
      </h1>
      <div class="hidden md:flex">
        <CButton
          v-if="isAutoBidderPage && hasStoreId"
          type="primary"
          class="h-10"
          @click="handleOpenCreateCompanyModal"
        >
          <i class="icon-plus-circle text-2xl" />
          {{ $t("create_company") }}
        </CButton>
        <CButton
          v-else-if="isDashboardPage"
          type="primary"
          class="h-10"
          @click="showApiKeyModal = true"
        >
          <i class="icon-pencil-edit-01 text-2xl" />
          {{ t("api_key") }}
        </CButton>
      </div>
    </div>
    <div class="flex items-center gap-1 sm:gap-3 shrink-0">
      <!-- Tariff -->
      <div
        v-if="isAuthenticated"
        class="hidden sm:flex items-center gap-2 rounded-xl h-8 sm:h-9 transition-colors"
        :class="[
          isTrial ? 'bg-amber-50 border border-amber-200' : 'bg-white',
          shouldShowTariffButton ? 'p-0.5 pl-3' : 'px-3',
        ]"
      >
        <i
          class="text-2xl"
          :class="isTrial ? 'icon-clock text-amber-500' : 'icon-crown-minimalistic-outline text-text-secondary'"
        />
        <span
          class="font-semibold text-xs sm:text-sm leading-[22px]"
          :class="isTrial ? 'text-amber-700' : 'text-text-primary'"
        >
          {{ isTrial ? $t('trial_period') : tariffDisplayName }}
        </span>
        <span
          class="text-xs sm:text-sm leading-[22px] hidden sm:inline"
          :class="isTrial ? 'text-amber-600' : 'text-text-regular'"
        >
          {{ tariffDuration }}
        </span>
        <CButton
          v-if="shouldShowTariffButton"
          class="!h-8 sm:!h-9 !bg-[#EFE8FB] !text-[#9117EF] !rounded-[11px] !text-[13px] !font-semibold !border-[#EFE8FB] hover:!bg-[#E4D3FF] hover:!border-[#E4D3FF]"
          @click="handleTariffButtonClick"
        >
          <i class="icon-cash text-2xl" />
          {{ tariffButtonText }}
        </CButton>
      </div>

      <!-- Calendar target -->
      <div
        id="dashboard-calendar-target"
        class="hidden lg:block min-w-0 shrink"
      />

      <!-- Notifications Dropdown - Hidden -->
      <div v-if="false && isAuthenticated">
        <ElDropdown
          ref="notificationsDropdownRef"
          trigger="click"
          :teleported="false"
          placement="bottom-end"
          :offset="12"
          popper-class="notifications-dropdown-popper"
          @visible-change="handleNotificationsDropdownVisible"
        >
          <span
            class="el-dropdown-link flex items-center justify-center relative cursor-pointer p-2 rounded-lg hover:bg-gray-100 transition-colors"
            style="cursor: pointer"
          >
            <i class="icon-notification text-2xl text-text-secondary" />
            <span
              v-if="unreadCount > 0"
              class="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] rounded-full min-w-[18px] h-[18px] flex items-center justify-center font-semibold px-1"
            >
              {{ unreadCount > 99 ? "99+" : unreadCount }}
            </span>
          </span>
          <template #dropdown>
            <ElDropdownMenu class="notifications-dropdown">
              <div class="px-4 py-3 border-b border-border-lighter">
                <div class="flex items-center justify-between">
                  <h3 class="font-semibold text-text-primary text-sm">
                    {{ t("notifications") }}
                  </h3>
                  <ElButton
                    v-if="recentNotifications.length > 0 && unreadCount > 0"
                    text
                    size="small"
                    :loading="isMarkingAllAsRead"
                    @click="handleMarkAllAsRead"
                  >
                    {{ t("mark_all_as_read") }}
                  </ElButton>
                </div>
              </div>
              <div
                ref="scrollContainer"
                v-loading="isLoadingNotifications"
                class="max-h-[400px] overflow-y-auto px-2"
              >
                <template v-if="!isLoadingNotifications">
                  <template v-if="recentNotifications.length > 0">
                    <ElDropdownItem
                      v-for="notification in recentNotifications"
                      :key="notification.id"
                      class="notification-item"
                      @click="handleNotificationClick(notification)"
                    >
                      <div class="flex items-start gap-3 w-full">
                        <div
                          v-if="!notification.is_read"
                          class="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0 mt-1.5"
                        />
                        <div v-else class="w-2 h-2 flex-shrink-0 mt-1.5" />
                        <div class="flex-1 min-w-0">
                          <div
                            class="font-medium text-sm text-text-primary mb-1"
                            :class="{
                              'font-semibold': !notification.is_read,
                              'font-normal': notification.is_read,
                            }"
                          >
                            {{ notification.title }}
                          </div>
                          <div
                            class="text-xs text-text-secondary mb-1.5 line-clamp-2 leading-relaxed"
                          >
                            {{ notification.message }}
                          </div>
                          <div class="text-xs text-text-secondary">
                            {{
                              formatNotificationDate(notification.created_at)
                            }}
                          </div>
                        </div>
                      </div>
                    </ElDropdownItem>
                    <!-- Load more trigger -->
                    <div
                      v-if="hasMore"
                      ref="loadMoreTrigger"
                      class="h-4 flex items-center justify-center"
                    >
                      <div
                        v-if="isLoadingMore"
                        class="text-xs text-text-secondary"
                      >
                        {{ t("loading") }}...
                      </div>
                    </div>
                  </template>
                  <div
                    v-else
                    class="px-4 py-8 text-center text-text-secondary text-sm"
                  >
                    {{ t("no_notifications") }}
                  </div>
                </template>
              </div>
              <div
                v-if="recentNotifications.length > 0"
                class="px-4 py-2 border-t border-border-lighter text-center"
              >
                <ElButton
                  text
                  size="small"
                  class="w-full"
                  @click="handleViewAllClick"
                >
                  {{ t("view_all_notifications") }}
                </ElButton>
              </div>
            </ElDropdownMenu>
          </template>
        </ElDropdown>
      </div>

      <!-- Language Switcher -->
      <CLanguageSwitcher />

      <!-- Profile Dropdown -->
      <div v-if="isAuthenticated">
        <ElDropdown ref="dropdownRef" trigger="click" :teleported="false">
          <span class="el-dropdown-link flex items-center gap-2">
            <!-- <CAvatar
              :size="40"
              :name="userFullName"
              class="!bg-white !text-text-primary"
            /> -->
            <img
              src="@/shared/assets/svg/user.svg"
              alt="user-avatar"
              class="h-9"
            >
            <span
              class="font-medium text-text-priamry text-sm leading-[22px] hidden lg:block whitespace-nowrap"
            >
              {{ userFullName }}
            </span>
            <i class="icon-chevron-down text-2xl hidden lg:block" />
          </span>
          <template #dropdown>
            <ElDropdownMenu>
              <ElDropdownItem class="flex items-center gap-2">
                <CAvatar
                  :size="32"
                  class="!bg-primary-light-9 !text-text-primary !text-xs border"
                  :name="userFullName"
                />
                <p
                  class="flex flex-col gap-0.5 font-medium text-text-priamry text-sm leading-[22px]"
                >
                  <span>{{ userFullName }}</span>
                  <span class="text-text-regular text-xs leading-[14px]">
                    {{ userEmail }}
                  </span>
                </p>
              </ElDropdownItem>
              <ElDivider class="w-[calc(100%-32px)] mx-auto !my-2" />
              <ElDropdownItem @click="navigateTo('/profile')">
                <div class="flex items-center gap-2">
                  <i class="icon-user-circle-02 text-2xl" />
                  {{ t("profile") }}
                </div>
              </ElDropdownItem>
              <ElDropdownItem @click="navigateTo('/tariffs')">
                <div class="flex items-center gap-2">
                  <i class="icon-calculate text-2xl" />
                  {{ t("tariffs") }}
                </div>
              </ElDropdownItem>
              <!-- TODO: Временно скрыт по запросу клиента, вернуть позже -->
              <!-- <ElDropdownItem
                @click="navigateTo('/profile?activeTab=referral')"
              >
                <div class="flex items-center gap-2">
                  <i class="icon-gift text-2xl"></i>
                  {{ t("referral_program") }}
                </div>
              </ElDropdownItem> -->
              <ElDropdownItem
                class="flex items-center gap-2"
                @click="showLogOutModal = true"
              >
                <i class="icon-logout-01 text-2xl" />
                {{ t("logout") }}
              </ElDropdownItem>
            </ElDropdownMenu>
          </template>
        </ElDropdown>
      </div>
    </div>

    <Teleport to="body">
      <CDialog
        v-model="showLogOutModal"
        :title="t('logout_from_account')"
        class="sm:!w-[360px] !w-[70%]"
      >
        <p class="text-sm text-text-secondary leading-none font-normal">
          {{ t("logout_modal_description") }}
        </p>
        <template #footer>
          <div class="flex items-center justify-end">
            <ElButton
              size="large"
              class="h-10 w-full"
              @click="showLogOutModal = false"
            >
              {{ t("cancel") }}
            </ElButton>
            <ElButton
              size="large"
              type="danger"
              class="h-10 w-full"
              @click="handleLogout"
            >
              <i class="icon-logout-01 text-2xl mr-2" />
              <span>{{ t("logout") }}</span>
            </ElButton>
          </div>
        </template>
      </CDialog>

      <!-- API Key Modal -->
      <CUpdateApiKeyModal v-model="showApiKeyModal" />

      <!-- Notification Detail Modal -->
      <CNotificationDetailModal
        v-model="showNotificationModal"
        :notification="selectedNotification"
      />
    </Teleport>
  </div>

  <!-- Mobile 2-Row Header -->
  <div class="flex sm:hidden flex-col gap-2 py-3 px-4 border-b">
    <!-- Row 1: Logo + Account -->
    <div class="flex items-center justify-between">
      <RouterLink to="/dashboard">
        <img src="@/shared/assets/svg/logo.svg" alt="MA" class="h-6" >
      </RouterLink>
      <div class="flex items-center gap-1.5">
        <!-- Tariff badge — compact -->
        <div
          v-if="isAuthenticated"
          class="flex items-center gap-1 rounded-lg h-7 px-2 transition-colors"
          :class="isTrial ? 'bg-amber-50 border border-amber-200' : 'bg-white'"
        >
          <i
            class="text-lg"
            :class="isTrial ? 'icon-clock text-amber-500' : 'icon-crown-minimalistic-outline text-text-secondary'"
          />
          <span
            class="text-xs font-semibold"
            :class="isTrial ? 'text-amber-700' : 'text-text-primary'"
          >
            {{ isTrial ? $t('trial_period') : tariffDisplayName }}
          </span>
        </div>
        <!-- Language -->
        <CLanguageSwitcher />
        <!-- Avatar dropdown — existing component reuse -->
        <div v-if="isAuthenticated">
          <ElDropdown ref="dropdownRef" trigger="click" :teleported="false">
            <span class="el-dropdown-link flex items-center gap-2">
              <img
                src="@/shared/assets/svg/user.svg"
                alt="user-avatar"
                class="h-9"
              >
            </span>
            <template #dropdown>
              <ElDropdownMenu>
                <ElDropdownItem class="flex items-center gap-2">
                  <CAvatar
                    :size="32"
                    class="!bg-primary-light-9 !text-text-primary !text-xs border"
                    :name="userFullName"
                  />
                  <p
                    class="flex flex-col gap-0.5 font-medium text-text-priamry text-sm leading-[22px]"
                  >
                    <span>{{ userFullName }}</span>
                    <span class="text-text-regular text-xs leading-[14px]">
                      {{ userEmail }}
                    </span>
                  </p>
                </ElDropdownItem>
                <ElDivider class="w-[calc(100%-32px)] mx-auto !my-2" />
                <ElDropdownItem @click="navigateTo('/profile')">
                  <div class="flex items-center gap-2">
                    <i class="icon-user-circle-02 text-2xl" />
                    {{ t("profile") }}
                  </div>
                </ElDropdownItem>
                <ElDropdownItem @click="navigateTo('/tariffs')">
                  <div class="flex items-center gap-2">
                    <i class="icon-calculate text-2xl" />
                    {{ t("tariffs") }}
                  </div>
                </ElDropdownItem>
                <ElDropdownItem
                  class="flex items-center gap-2"
                  @click="showLogOutModal = true"
                >
                  <i class="icon-logout-01 text-2xl" />
                  {{ t("logout") }}
                </ElDropdownItem>
              </ElDropdownMenu>
            </template>
          </ElDropdown>
        </div>
      </div>
    </div>

    <!-- Row 2: Burger + Title -->
    <div class="flex items-center gap-3">
      <button
        class="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
        @click="$emit('toggleSidebar')"
      >
        <svg
          class="w-5 h-5 text-gray-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M4 6h16M4 12h16M4 18h16"
          />
        </svg>
      </button>
      <h1 class="text-lg font-bold text-text-primary">
        {{ t(title) }}
      </h1>
    </div>
  </div>
</template>

<style scoped>
:deep(.el-dropdown-menu) {
  z-index: 9999 !important;
}

:deep(.el-popper) {
  z-index: 9999 !important;
}

:deep(.notifications-dropdown) {
  min-width: 320px;
  max-width: 400px;
}

:deep(.notifications-dropdown-popper) {
  margin-right: 12px !important;
}

:deep(.notifications-dropdown-popper .el-popper__arrow) {
  right: 20px !important;
}

:deep(.notification-item) {
  padding: 12px 14px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background-color 0.2s;
  margin: 0 !important;
  margin-left: 4px !important;
  margin-right: 4px !important;
  border-radius: 6px;
}

:deep(.notification-item:hover) {
  background-color: #f5f7fa;
}

:deep(.notification-item:last-child) {
  border-bottom: none;
}

:deep(.notifications-dropdown .el-dropdown-menu__item) {
  padding: 0;
}

:deep(.line-clamp-2) {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

:deep(.el-dropdown-link) {
  cursor: pointer !important;
}
</style>
